import React, { useRef, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Html } from '@react-three/drei'
import { Play } from 'lucide-react'
import * as THREE from 'three'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Từ thông & cảm ứng điện từ (Lớp 12 — Bài 14) — mô hình 3D: nam châm chuyển động dọc trục
// cuộn dây → từ thông Φ biến thiên → suất điện động & dòng điện cảm ứng (kim điện kế lệch).
const phiOf = (x: number) => 120 / (1 + (x / 1.1) ** 2)   // x = vị trí cực phải nam châm so với tâm cuộn

function Scene({ manualXRef }: { manualXRef: React.MutableRefObject<number> }) {
  const magnet = useRef<THREE.Group>(null)
  const needle = useRef<THREE.Group>(null)
  const info = useRef<HTMLSpanElement>(null)
  const arrow = useRef<THREE.Group>(null)
  const prevPhi = useRef(0), emf = useRef(0), nd = useRef(0)
  useFrame(() => {
    // Chỉ KÉO TAY: vị trí nam châm do người dùng kéo
    const mx = manualXRef.current
    if (magnet.current) magnet.current.position.x = mx
    const phi = phiOf(mx + 0.9)                        // cực phải nam châm (gần cuộn)
    const e = -(phi - prevPhi.current) * 6; prevPhi.current = phi
    emf.current = emf.current * 0.7 + e * 0.3
    nd.current += (Math.max(-1, Math.min(1, emf.current / 4)) - nd.current) * 0.25
    if (needle.current) needle.current.rotation.z = -nd.current * 1.1
    if (arrow.current) { arrow.current.visible = Math.abs(emf.current) > 0.25; arrow.current.scale.x = emf.current >= 0 ? 1 : -1 }
    if (info.current) info.current.textContent = `Φ ≈ ${phi.toFixed(0)}   ·   e cảm ứng ≈ ${emf.current.toFixed(1)}`
  })
  const rings = Array.from({ length: 9 }, (_, i) => -1.0 + i * 0.25)
  return (
    <>
      <ambientLight intensity={0.8} />
      <directionalLight position={[4, 6, 5]} intensity={0.7} />
      {/* Cuộn dây: các vòng dây quanh trục x */}
      {rings.map((x, i) => (
        <mesh key={i} position={[x, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <torusGeometry args={[1, 0.05, 12, 32]} /><meshStandardMaterial color="#b45309" metalness={0.6} roughness={0.4} />
        </mesh>
      ))}
      {/* Nam châm: N (đỏ) | S (xanh), cực phải hướng vào cuộn */}
      <group ref={magnet} position={[-2.8, 0, 0]}>
        <mesh position={[-0.45, 0, 0]}><boxGeometry args={[0.9, 0.55, 0.55]} /><meshStandardMaterial color="#ef4444" /></mesh>
        <mesh position={[0.45, 0, 0]}><boxGeometry args={[0.9, 0.55, 0.55]} /><meshStandardMaterial color="#3b82f6" /></mesh>
        <Html position={[-0.45, 0.55, 0]} center><span style={{ color: '#b91c1c', fontWeight: 800 }}>N</span></Html>
        <Html position={[0.45, 0.55, 0]} center><span style={{ color: '#1d4ed8', fontWeight: 800 }}>S</span></Html>
      </group>
      {/* 2 đầu dây NỐI VÀO 2 đầu cuộn dây (x = ±1) rồi xuống điện kế */}
      <mesh position={[-1, -1, 0]}><sphereGeometry args={[0.1, 12, 12]} /><meshStandardMaterial color="#b45309" /></mesh>
      <mesh position={[1, -1, 0]}><sphereGeometry args={[0.1, 12, 12]} /><meshStandardMaterial color="#b45309" /></mesh>
      <mesh position={[-1, -1.72, 0]}><boxGeometry args={[0.06, 1.44, 0.06]} /><meshStandardMaterial color="#64748b" /></mesh>
      <mesh position={[1, -1.72, 0]}><boxGeometry args={[0.06, 1.44, 0.06]} /><meshStandardMaterial color="#64748b" /></mesh>
      <mesh position={[0, -2.44, 0]}><boxGeometry args={[2, 0.06, 0.06]} /><meshStandardMaterial color="#64748b" /></mesh>
      {/* Điện kế 3D (vỏ máy + mặt số + kim) */}
      <group position={[0, -2.62, 0]}>
        <mesh><boxGeometry args={[1.9, 1.4, 0.5]} /><meshStandardMaterial color="#334155" metalness={0.3} roughness={0.6} /></mesh>
        <mesh position={[0, 0.1, 0.255]}><boxGeometry args={[1.7, 1.2, 0.02]} /><meshStandardMaterial color="#1e293b" /></mesh>
        <mesh position={[0, 0.12, 0.27]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.58, 0.58, 0.04, 40]} /><meshStandardMaterial color="#f8fafc" /></mesh>
        <group ref={needle} position={[0, 0.12, 0.31]}>
          <mesh position={[0, 0.34, 0]}><boxGeometry args={[0.045, 0.68, 0.03]} /><meshStandardMaterial color="#dc2626" /></mesh>
        </group>
        <mesh position={[0, 0.12, 0.33]}><sphereGeometry args={[0.06, 12, 12]} /><meshStandardMaterial color="#0f172a" /></mesh>
        <Html position={[0, -0.52, 0.3]} center><span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 11 }}>Điện kế (G)</span></Html>
        <Html position={[-0.5, 0.6, 0.3]} center><span style={{ color: '#475569', fontSize: 12, fontWeight: 700 }}>−</span></Html>
        <Html position={[0, 0.78, 0.3]} center><span style={{ color: '#475569', fontSize: 11 }}>0</span></Html>
        <Html position={[0.5, 0.6, 0.3]} center><span style={{ color: '#475569', fontSize: 12, fontWeight: 700 }}>+</span></Html>
      </group>
      {/* mũi tên chiều dòng cảm ứng (đảo theo dấu e) */}
      <group ref={arrow} position={[0, -1.95, 0.2]}>
        <Html center><span style={{ color: '#16a34a', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>→ dòng cảm ứng</span></Html>
      </group>
      {/* số liệu */}
      <Html position={[0, 1.7, 0]} center><span ref={info} style={{ color: '#0f172a', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.7)', padding: '2px 6px', borderRadius: 6 }} /></Html>
      <OrbitControls enablePan={false} enableRotate={false} target={[0, -0.7, 0]} minDistance={5} maxDistance={15} />
    </>
  )
}

const EMInductionSimulation: React.FC<Props> = ({
  title = 'Từ thông & hiện tượng cảm ứng điện từ (3D)',
  description = 'KÉO nam châm trái/phải dọc trục cuộn dây → từ thông Φ biến thiên → sinh suất điện động & dòng điện cảm ứng (kim điện kế lệch). Đứng yên → Φ không đổi → không có dòng cảm ứng.',
  onComplete,
}) => {
  const manualXRef = useRef(-2.8)
  const dragRef = useRef(false)
  const firstRef = useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }
  const onDown = () => { dragRef.current = true; touch() }
  const onMove = (e: React.PointerEvent) => { if (dragRef.current) manualXRef.current = Math.max(-4, Math.min(4, manualXRef.current + e.movementX * 0.012)) }
  const onUp = () => { dragRef.current = false }
  return (
    <div className="rounded-2xl border border-emerald-200 bg-white p-4 my-3">
      <h4 className="font-bold text-emerald-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="rounded-lg overflow-hidden border border-gray-100 cursor-ew-resize" style={{ height: 340, background: 'linear-gradient(#f8fafc,#eef2f7)' }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
        <Canvas camera={{ position: [3.5, 1.5, 7], fov: 50 }} onCreated={touch}>
          <Scene manualXRef={manualXRef} />
        </Canvas>
      </div>
      <div className="flex items-center gap-3 mt-3 text-sm">
        <button onClick={() => { touch(); manualXRef.current = -2.8 }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-emerald-200 text-emerald-700 font-bold hover:bg-emerald-50"><Play className="w-4 h-4" /> Đưa nam châm về vị trí đầu</button>
        <span className="text-gray-500">✋ Bấm và kéo trái/phải để dịch nam châm</span>
      </div>
      <p className="text-[11px] text-gray-400 mt-2"><b>Kéo trái/phải</b> để dịch nam châm — kéo nhanh → e &amp; dòng cảm ứng lớn (kim lệch nhiều); đứng yên → Φ không đổi → không có dòng; đổi chiều kéo → dòng cảm ứng đảo chiều.</p>
    </div>
  )
}
export default EMInductionSimulation
